import type { Ref } from "vue"
import { ref, watch } from "vue"
import { defineStore, storeToRefs } from "pinia"
import { webSocketStore } from "stores/web-socket-store"
import type { BuzzSentiment } from "@stockpulse/typescript-axios/types/buzz-sentiment"
import type { TitlesBuzzSentimentDataCollection } from "src/helper/Auxiliary"
import { Auxiliary } from "src/helper/Auxiliary"
import { useBufferedThrottleFn } from "src/helper/BufferedThrottle"
import { RealTimeReportType } from "src/helper/ReportHelper"
import type { BuzzSentimentUpdate } from "@stockpulse/typescript-axios/types/buzz-sentiment-update"
import type { Tick as TickDto } from "@stockpulse/typescript-axios"
import * as Sentry from "@sentry/vue"

export type SubscriptionId = string

export interface WsSubscription {
    titleIds: number[]
    subscriptionId: SubscriptionId
    partition: string
}

export interface WsTickSubscription {
    titleIds: number[]
    subscriptionId: SubscriptionId
}

export type Tick = { titleId: number; t: number; value: number }

export interface TitleTickCollection {
    [key: number]: Tick
}

export const useRealTimeDataStore = defineStore("realTimeDataStore", () => {
    const webSocket = webSocketStore()
    const { isSocketReady } = storeToRefs(webSocket)

    const titlesRealtimeData: Ref<Map<RealTimeReportType, TitlesBuzzSentimentDataCollection>> = ref(new Map())
    const pendingBuzzSentimentSubscriptions: Ref<WsSubscription[]> = ref([])
    const currentBuzzSentimentSubscriptions: Ref<WsSubscription[]> = ref([])

    const titlesTick: Ref<TitleTickCollection> = ref({})
    const pendingTickSubscriptions: Ref<WsTickSubscription[]> = ref([])
    const currentTickSubscriptions: Ref<WsTickSubscription[]> = ref([])

    const onWebsocketOpen = (isOpen: boolean): void => {
        if (isOpen) {
            emitSubscribeToUnprocessedTitles()
        }
    }
    watch(isSocketReady, onWebsocketOpen, { immediate: true })

    const bufferedTitleBuzzSentimentUpdate = (newValues: BuzzSentimentUpdate[]): void => {
        const reducedBuffer = Object.values(
            newValues.reduce((acc: { [key: string]: BuzzSentimentUpdate }, curr: BuzzSentimentUpdate) => {
                const key = `${curr.id}-${curr.partition}`
                acc[key] = curr
                return acc
            }, {})
        )

        const currentValues = new Map([...titlesRealtimeData.value])

        reducedBuffer.forEach((buzzUpdate) => {
            const sentimentScore = Auxiliary.calculateSentimentScore(buzzUpdate.s || 0) / 100
            const sentimentIcon = Auxiliary.getIconClassBySentimentValue(sentimentScore)
            const sentimentIconColor = Auxiliary.getColorForSentimentScore(sentimentScore)

            const buzzSentimentData = {
                id: buzzUpdate.id,
                buzz: buzzUpdate.b,
                sentiment: buzzUpdate.s || 0,
                sentimentIcon,
                sentimentIconColor,
                sentimentScore,
                time: buzzUpdate.t,
            }
            const partition = (buzzUpdate.partition as RealTimeReportType) || RealTimeReportType.DEFAULT
            const partitionValues = currentValues.get(partition)
            if (partitionValues) {
                partitionValues[buzzSentimentData.id] = buzzSentimentData
                currentValues.set(partition, partitionValues)
            } else {
                currentValues.set(partition, { [buzzSentimentData.id]: buzzSentimentData })
            }
        })

        titlesRealtimeData.value = currentValues
    }

    const throttleUpdateTitleBuzzSentiment = useBufferedThrottleFn(bufferedTitleBuzzSentimentUpdate, 4_000)

    function emitSubscribeToUnprocessedTitles(): void {
        if (pendingBuzzSentimentSubscriptions.value.length > 0) {
            pendingBuzzSentimentSubscriptions.value.forEach((add) => subscribeTitleUpdates(add.titleIds))
            pendingBuzzSentimentSubscriptions.value = []
        }

        if (pendingTickSubscriptions.value.length > 0) {
            pendingTickSubscriptions.value.forEach((add) => subscribeTitleTicks(add.titleIds))
            pendingTickSubscriptions.value = []
        }
    }

    function subscribeTitleUpdates(titleIds: number[], partition = "realtime"): SubscriptionId {
        const subscriptionId = generateSubscriptionId()
        if (!isSocketReady.value) {
            pendingBuzzSentimentSubscriptions.value.push({ titleIds, subscriptionId, partition })
            return subscriptionId
        }

        webSocket.emitSubscribeTitlesPartition(
            titleIds,
            partition,
            handleWebsocketTitleUpdateMessage,
            websocketSubscribeTitleIdsCallback
        )
        currentBuzzSentimentSubscriptions.value.push({ titleIds, subscriptionId, partition })
        return subscriptionId
    }

    function subscribeTitleTicks(titleIds: number[]): SubscriptionId {
        unsubscribeTitlesTicks(currentTickSubscriptions.value.map((subscription) => subscription.subscriptionId))

        const subscriptionId = generateSubscriptionId()
        if (!isSocketReady.value) {
            pendingTickSubscriptions.value.push({ titleIds, subscriptionId })
            return subscriptionId
        }

        webSocket.emitSubscribeTitlesTicks(titleIds, handleWebsocketTitlesTicks, websocketSubscribeTitlesTicksCallback)
        currentTickSubscriptions.value.push({ titleIds, subscriptionId })

        return subscriptionId
    }

    function generateSubscriptionId(): SubscriptionId {
        while (true) {
            const timestamp = new Date().getTime()
            const randomNum = Math.floor(Math.random() * 1000000)
            const subscriptionId = `${timestamp}-${randomNum}`
            const found =
                currentBuzzSentimentSubscriptions.value.findIndex((sub) => sub.subscriptionId === subscriptionId) !==
                    -1 ||
                pendingBuzzSentimentSubscriptions.value.findIndex((sub) => sub.subscriptionId === subscriptionId) !== -1
            if (!found) {
                return subscriptionId
            }
        }
    }

    function unsubscribeTitleUpdates(subscriptionIds: SubscriptionId[]): void {
        subscriptionIds.forEach((subscriptionId) => {
            const wsPendingSubscriptionIndex = pendingBuzzSentimentSubscriptions.value.findIndex(
                (ws) => ws.subscriptionId === subscriptionId
            )
            if (wsPendingSubscriptionIndex !== -1) {
                pendingBuzzSentimentSubscriptions.value.slice(wsPendingSubscriptionIndex, 1)
            }

            const wsSubscriptionIndex = currentBuzzSentimentSubscriptions.value.findIndex(
                (ws) => ws.subscriptionId === subscriptionId
            )
            if (wsSubscriptionIndex === -1) {
                return
            }
            const wsSubscription = currentBuzzSentimentSubscriptions.value[wsSubscriptionIndex]

            const currentTitleIds = getUniqueTitleIds(currentBuzzSentimentSubscriptions.value, wsSubscription.partition)

            currentBuzzSentimentSubscriptions.value.splice(wsSubscriptionIndex, 1)

            const remainingTitleIds = getUniqueTitleIds(
                currentBuzzSentimentSubscriptions.value,
                wsSubscription.partition
            )

            const titleIdsToUnsubscribe = currentTitleIds.filter((curTitle) => !remainingTitleIds.includes(curTitle))

            webSocket.emitUnsubscribeTitles(titleIdsToUnsubscribe)
        })
    }

    function unsubscribeTitlesTicks(subscriptionIds: SubscriptionId[]): void {
        subscriptionIds.forEach((subscriptionId) => {
            const wsPendingSubscriptionIndex = pendingTickSubscriptions.value.findIndex(
                (ws) => ws.subscriptionId === subscriptionId
            )
            if (wsPendingSubscriptionIndex !== -1) {
                pendingTickSubscriptions.value.slice(wsPendingSubscriptionIndex, 1)
            }

            const wsSubscriptionIndex = currentTickSubscriptions.value.findIndex(
                (ws) => ws.subscriptionId === subscriptionId
            )
            if (wsSubscriptionIndex === -1) {
                return
            }
            const currentTitleIds = getUniqueTickTitleIds(currentTickSubscriptions.value)

            currentTickSubscriptions.value.splice(wsSubscriptionIndex, 1)

            const remainingTitleIds = getUniqueTickTitleIds(currentTickSubscriptions.value)

            const titleIdsToUnsubscribe = currentTitleIds.filter((curTitle) => !remainingTitleIds.includes(curTitle))

            webSocket.emitUnsubscribeTitles(titleIdsToUnsubscribe)
        })
    }

    function getUniqueTitleIds(wsRequests: WsSubscription[], partition: string): number[] {
        const uniqueIdsSet = new Set<number>()
        wsRequests.forEach((wsRequest) => {
            if (partition !== wsRequest.partition) {
                return
            }
            wsRequest.titleIds.forEach((id) => uniqueIdsSet.add(id))
        })
        return Array.from(uniqueIdsSet)
    }

    function getUniqueTickTitleIds(wsRequests: WsTickSubscription[]): number[] {
        const uniqueIdsSet = new Set<number>()
        wsRequests.forEach((wsRequest) => {
            wsRequest.titleIds.forEach((id) => uniqueIdsSet.add(id))
        })
        return Array.from(uniqueIdsSet)
    }

    function websocketSubscribeTitleIdsCallback(error: unknown, data: unknown): void {
        if (error) {
            if (typeof error === "object" && "code" in error && error.code === 404) {
                return
            }
            Sentry.captureException(error)
            return
        }
        let dataArray: BuzzSentiment[]
        if (!Array.isArray(data)) {
            dataArray = [data as BuzzSentiment]
        } else {
            dataArray = data as BuzzSentiment[]
        }
        bufferedTitleBuzzSentimentUpdate(dataArray)
    }

    function websocketSubscribeTitlesTicksCallback(error: unknown, data: unknown): void {
        if (error) {
            if (typeof error === "object" && "code" in error && error.code === 404) {
                return
            }
            Sentry.captureException(error)
            return
        }

        const tickArray: TickDto[] = !Array.isArray(data) ? [data as TickDto] : (data as TickDto[])
        tickArray.forEach((tick) => {
            titlesTick.value[tick.id] = {
                titleId: tick.id,
                value: tick.l,
                t: tick.t * 1000,
            }
        })
    }

    function handleWebsocketTitleUpdateMessage(data: unknown, error: unknown): void {
        if (error) {
            if (typeof error === "object" && "code" in error && error.code === 404) {
                return
            }
            Sentry.captureException(error)
            return
        }
        throttleUpdateTitleBuzzSentiment([data as BuzzSentiment])
    }

    function handleWebsocketTitlesTicks(data: unknown, error: unknown): void {
        if (error) {
            if (typeof error === "object" && "code" in error && error.code === 404) {
                return
            }
            Sentry.captureException(error)
        }
        if (error || data === null) {
            return
        }

        const tickArray: TickDto[] = !Array.isArray(data) ? [data as TickDto] : (data as TickDto[])
        tickArray.forEach((tick) => {
            const tickTime = tick.t * 1000
            if (titlesTick.value[tick.id]?.t && titlesTick.value[tick.id]?.t >= tickTime) {
                return
            }
            titlesTick.value[tick.id] = {
                titleId: tick.id,
                value: tick.l,
                t: tickTime,
            }
        })
    }

    return {
        titlesRealtimeData,
        titlesTick,
        subscribeTitleUpdates,
        unsubscribeTitleUpdates,
        subscribeTitleTicks,
        unsubscribeTitlesTicks,
    }
})
